/**
 * Soulumo Foundations · CARE FOR ME
 *
 * Mar 2026 · Michael's Care-Package direction.
 *
 *   Primary question: "What do you need right now?"
 *
 * Instead of a library organised by SECTION (Morning · Rest · etc.),
 * this page re-presents the existing Foundations by felt NEED:
 *
 *   One minute for me   → One Minute for Me (60s · new daily reset)
 *   I need a moment     → A Moment for Me (60s) · Quick Reset (90s)
 *   I'm overwhelmed     → For a Difficult Day (5m) · Quick Reset
 *   I need to settle    → Peace 3m / 7m
 *   I feel alone        → You Are Not Alone (7m)
 *   I need to rest      → Rest 3m / 7m · Night 20m / 8h
 *   I need to let something go → For a Difficult Day → Peace 7m
 *   I need to remember myself  → Self-Belief · A Little Kindness for Your Body
 *
 * Everything existing is reused; no track is duplicated or renamed.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import SoulumoWord from "../../components/SoulumoWord";
import FoundationsResetRitual from "./FoundationsResetRitual";
import { TOK, SERIF, SANS } from "./foundationsTokens";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");
const auth = () => {
  const t = getToken();
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

// Ordered need-groups. Each entry describes WHICH existing items to
// surface and in what order. We never invent copy for an item — we
// show the item's existing label + blurb. `special` lets us wire
// one entry to the client-side Quick Reset ritual.
const NEEDS = [
  {
    key: "one_minute",
    felt: "One minute for me",
    hint: "Feet · breath · heart · permission. For one minute, you are allowed to be cared for too.",
    match: (it) => it.slot === "one_minute_for_me",
  },
  {
    key: "moment",
    felt: "I need a moment",
    hint: "A short pause, right where you are.",
    match: (it) =>
      it.slot === "moment_for_me" ||
      (it.slot === "reset" && it.reset_kind === "breath"),
  },
  {
    key: "overwhelmed",
    felt: "I'm overwhelmed",
    hint: "Something steadying for the days that feel like too much.",
    match: (it) => it.slot === "difficult_day" || it.slot === "reset",
  },
  {
    key: "settle",
    felt: "I need to settle",
    hint: "A gentle return to presence, steadiness and clear boundaries.",
    match: (it) => it.slot === "peace",
  },
  {
    key: "alone",
    felt: "I feel alone",
    hint: "Companion energy. You do not have to carry everything alone.",
    match: (it) => it.slot === "you_are_not_alone",
  },
  {
    key: "rest",
    felt: "I need to rest",
    hint: "Put the day down. Nothing to fix.",
    match: (it) => it.slot === "rest" || it.slot === "night_master",
    // Oct 3 2026 · Michael asked to surface Reclaim's Sleep Biohacking
    // area here (Overview · Protocol · Checklist · Sleep Installations
    // + Sleep Score / Recovery). Ported to /foundations/sleep with
    // Soulumo visual tokens so it reads as a native Foundations
    // sub-page — the backend data stays shared.
    extraLinks: [
      {
        key: "sleep-biohacking",
        label: "Sleep Biohacking",
        blurb: "Overview, protocol, checklist and sleep installations — the full nightly system.",
        hintBadge: "A deeper system",
        href: "/foundations/sleep",
      },
    ],
  },
  {
    key: "let_go",
    felt: "I need to let something go",
    hint: "A steadying arc from Difficult Day through Peace.",
    match: (it) =>
      (it.slot === "difficult_day") || (it.slot === "peace" && it.duration_minutes === 7),
  },
  {
    key: "remember_self",
    felt: "I need to remember myself",
    hint: "A kinder inner voice. The person beneath everything.",
    match: (it) =>
      it.slot === "self_belief" || it.slot === "kindness_for_body",
  },
];

function fmtDuration(it) {
  if (!it) return "";
  const secs = Number(it.duration_seconds || 0);
  const mins = Number(it.duration_minutes || 0);
  if (secs > 0 && secs < 60) return `${secs} seconds`;
  if (mins >= 60) {
    const h = Math.round(mins / 60);
    return `${h} hour${h === 1 ? "" : "s"}`;
  }
  if (mins >= 1) return `${mins} minute${mins === 1 ? "" : "s"}`;
  return "";
}

const isReady = (it) =>
  it && it.audio_status === "ready" && (!!it.audio_url || !!it.client_ritual);

export default function CareForMePage() {
  const nav = useNavigate();
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuickReset, setShowQuickReset] = useState(false);
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/foundations/manifest`, auth());
        if (!cancelled) setManifest(r.data);
      } catch (_) {
        /* silent — the home will have already surfaced any auth issue */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const items = manifest?.items || [];
  const hasAccess = !!manifest?.has_access;

  const groups = useMemo(() => {
    return NEEDS.map((n) => {
      const matched = items.filter((it) => n.match(it) && isReady(it));
      // Order: shorter first, duplicates by slot grouped.
      matched.sort((a, b) => (a.duration_minutes || 0) - (b.duration_minutes || 0));
      return { ...n, items: matched };
    }).filter((g) => g.items.length > 0);
  }, [items]);

  const openItem = (it) => {
    if (!hasAccess) return;
    if (it.client_ritual === "quick_reset") {
      setShowQuickReset(true);
    } else {
      nav(`/foundations/play/${it.id}`);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: TOK.bg, color: TOK.creamDim }}
      >
        <p className="italic" style={{ fontFamily: SERIF }}>Preparing your Foundations…</p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: TOK.bg, color: TOK.cream }}
      data-testid="care-for-me-page"
    >
      <div className="max-w-md mx-auto px-5 pt-8 pb-24">
        {/* Header */}
        <div className="text-center">
          <SoulumoWord size="sm" />
          <div
            className="mt-2"
            style={{
              color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.6rem",
              letterSpacing: "0.42em", textTransform: "uppercase",
            }}
          >
            Care for me
          </div>
        </div>

        <h1
          className="mt-10 text-center italic leading-tight"
          style={{
            color: TOK.cream, fontFamily: SERIF,
            fontSize: "clamp(1.5rem, 5.5vw, 1.9rem)", fontWeight: 300,
          }}
          data-testid="care-for-me-headline"
        >
          What do you need right now?
        </h1>
        <p
          className="mt-4 text-center max-w-sm mx-auto"
          style={{
            color: TOK.creamDim, fontFamily: SANS,
            fontSize: "0.95rem", lineHeight: 1.6,
          }}
        >
          A little care for the one who is always caring for everyone else.
        </p>

        {/* Needs list */}
        <div className="mt-10 space-y-2" data-testid="care-for-me-needs">
          {groups.map((g) => {
            const open = openKey === g.key;
            return (
              <div
                key={g.key}
                className="rounded-2xl"
                style={{ background: TOK.cardBg, border: `1px solid ${TOK.cardBorder}` }}
                data-testid={`need-${g.key}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenKey(open ? null : g.key)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
                  style={{ minHeight: 56 }}
                  data-testid={`need-${g.key}-toggle`}
                >
                  <span>
                    <span
                      className="block italic"
                      style={{
                        color: TOK.cream, fontFamily: SERIF,
                        fontSize: "1.05rem", lineHeight: 1.3,
                      }}
                    >
                      {g.felt}
                    </span>
                    <span
                      className="block mt-1"
                      style={{
                        color: TOK.creamMist, fontFamily: SANS,
                        fontSize: "0.78rem", lineHeight: 1.4,
                      }}
                    >
                      {g.hint}
                    </span>
                  </span>
                  <span
                    aria-hidden
                    style={{
                      color: TOK.goldSoft, fontFamily: SANS, fontSize: "1.2rem",
                      transform: open ? "rotate(90deg)" : "rotate(0deg)",
                      transition: "transform 160ms ease",
                      flex: "0 0 auto",
                    }}
                  >
                    ›
                  </span>
                </button>
                {open && (
                  <div className="px-5 pb-4 space-y-2" data-testid={`need-${g.key}-tracks`}>
                    {g.items.map((it) => (
                      <button
                        type="button"
                        key={it.id}
                        onClick={() => openItem(it)}
                        disabled={!hasAccess}
                        className="w-full flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-left"
                        style={{
                          background: "rgba(224,169,122,0.08)",
                          border: `1px solid ${TOK.goldMist}`,
                          minHeight: 52,
                          opacity: hasAccess ? 1 : 0.55,
                        }}
                        data-testid={`need-${g.key}-track-${it.id}`}
                      >
                        <span>
                          <span
                            className="block"
                            style={{
                              color: TOK.cream, fontFamily: SANS,
                              fontSize: "0.95rem", fontWeight: 500,
                            }}
                          >
                            {it.label}
                          </span>
                          <span
                            className="block mt-0.5 italic"
                            style={{
                              color: TOK.creamMist, fontFamily: SERIF,
                              fontSize: "0.78rem",
                            }}
                          >
                            {it.blurb}
                          </span>
                        </span>
                        <span
                          className="uppercase"
                          style={{
                            color: TOK.goldSoft, fontFamily: SANS,
                            fontSize: "0.6rem", letterSpacing: "0.24em",
                            flex: "0 0 auto",
                          }}
                        >
                          {fmtDuration(it)} ›
                        </span>
                      </button>
                    ))}
                    {(g.extraLinks || []).map((lnk) => (
                      <button
                        type="button"
                        key={lnk.key}
                        onClick={() => hasAccess && nav(lnk.href)}
                        disabled={!hasAccess}
                        className="w-full flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-left"
                        style={{
                          background: "rgba(224,169,122,0.08)",
                          border: `1px solid ${TOK.goldMist}`,
                          minHeight: 52,
                          opacity: hasAccess ? 1 : 0.55,
                        }}
                        data-testid={`need-${g.key}-link-${lnk.key}`}
                      >
                        <span>
                          <span
                            className="block"
                            style={{
                              color: TOK.cream, fontFamily: SANS,
                              fontSize: "0.95rem", fontWeight: 500,
                            }}
                          >
                            {lnk.label}
                          </span>
                          <span
                            className="block mt-0.5 italic"
                            style={{
                              color: TOK.creamMist, fontFamily: SERIF,
                              fontSize: "0.78rem",
                            }}
                          >
                            {lnk.blurb}
                          </span>
                        </span>
                        <span
                          className="uppercase"
                          style={{
                            color: TOK.goldSoft, fontFamily: SANS,
                            fontSize: "0.6rem", letterSpacing: "0.24em",
                            flex: "0 0 auto",
                          }}
                        >
                          {lnk.hintBadge} ›
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!hasAccess && (
          <p
            className="mt-8 text-center italic"
            style={{ color: TOK.creamStrong, fontFamily: SERIF, fontSize: "0.95rem" }}
            data-testid="care-for-me-no-access"
          >
            Sign in with your Foundations invitation to open these.
          </p>
        )}

        <div className="mt-14 flex justify-center">
          <button
            onClick={() => nav("/foundations")}
            className="uppercase"
            style={{
              background: "transparent", color: TOK.creamMist, fontFamily: SANS,
              fontSize: "0.6rem", letterSpacing: "0.28em",
              padding: "12px 6px", borderBottom: `1px dashed ${TOK.goldMist}`,
              minHeight: 44,
            }}
            data-testid="care-for-me-back"
          >
            ← Back to Foundations
          </button>
        </div>
      </div>

      <FoundationsResetRitual
        isOpen={showQuickReset}
        onClose={() => setShowQuickReset(false)}
      />
    </div>
  );
}
